import encryption from '@/utils/encryption.ts'

import { ApiResponse, CollectionData, SingleItemData } from './types'

/**
 * Type guard to check if a response is an ApiResponse
 */
export function isApiResponse<T>(data: any): data is ApiResponse<T> {
  return (
    data && typeof data === 'object' && 'success' in data && 'message' in data && 'data' in data
  )
}

/**
 * Type guard to check if data is CollectionData
 */
export function isCollectionData<T>(data: any): data is CollectionData<T> {
  return (
    data &&
    typeof data === 'object' &&
    'page' in data &&
    'length' in data &&
    'count' in data &&
    'result' in data &&
    Array.isArray(data.result)
  )
}

/**
 * Type guard to check if data is SingleItemData
 */
export function isSingleItemData<T>(data: any): data is SingleItemData<T> {
  return data && typeof data === 'object' && 'result' in data && !Array.isArray(data.result)
}

/**
 * Extract the result array from CollectionData
 */
export function extractCollectionResult<T>(data: CollectionData<T>): T[] {
  return data.result
}

/**
 * Extract the result item from SingleItemData
 */
export function extractSingleResult<T>(data: SingleItemData<T>): T {
  return data.result
}

/**
 * Get pagination info from CollectionData
 */
export function getCollectionPagination<T>(data: CollectionData<T>) {
  return {
    page: data.page,
    length: data.length,
    count: data.count,
    totalPages: Math.ceil(data.count / data.length),
  }
}

export const stringifyGraph = (graph: Record<string, any>): string => {
  const process = (obj: any): string => {
    return Object.entries(obj)
      .map(([key, value]) => {
        if (value === true) return key
        if (typeof value === 'object' && value !== null) {
          return `${key}{${process(value)}}`
        }
        return key
      })
      .join(',')
  }

  const secret = import.meta.env.VITE_GRAPHSQL_SECRET || '0'
  return encryption.encrypt(`{${process(graph)}}`, secret)
}

/**
 * Encrypts a raw graph DSL string (e.g. grouped/aggregation graphs like
 * `{total.count,active(status=active).count}`) without object-based processing.
 */
export const stringifyRawGraph = (rawGraph: string): string => {
  const secret = import.meta.env.VITE_GRAPHSQL_SECRET || '0'
  return encryption.encrypt(rawGraph, secret)
}

const STORAGE_BASE_URL = import.meta.env.VITE_STORAGE_URL

export const getFileUrl = (path: string | File | Blob | null | undefined) => {
  if (!path) return ''
  if (typeof path === 'string') {
    if (path.startsWith('http')) return path
    return `${STORAGE_BASE_URL}/${path}`
  }
  if (path instanceof File || path instanceof Blob) {
    return URL.createObjectURL(path)
  }
  return ''
}
